import { Input } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { Check, Edit03, XClose } from '@insight-health-ai/roseate/icons';
import { Button } from '@insight-health-ai/roseate/components';
import classNames from 'classnames';

export type EditableFieldProps = {
  children?: React.ReactNode;
  text?: string;
  onSave: (value: string) => void;
  validate?: (value: string) => boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

const inputSizeMap = {
  sm: 'small',
  md: 'middle',
  lg: 'large',
} as const;

export const EditableField = ({
  children,
  text,
  onSave,
  validate,
  size = 'sm',
  className,
}: EditableFieldProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(text);

  const handleSave = async () => {
    await onSave(value || '');
    setIsEditing(false);
  };

  const isValid = useMemo(() => {
    return validate ? validate(value || '') : true;
  }, [value, validate]);

  const handleCancel = () => {
    setValue(text);
    setIsEditing(false);
  };

  return (
    <div
      ref={wrapperRef}
      className={classNames('flex items-center gap-1', className)}
    >
      {isEditing ? (
        <>
          <Input
            size={inputSizeMap[size]}
            className={classNames({
              '!border-danger': !isValid,
            })}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSave();
              }
            }}
          />
          <Button
            variant="text"
            size={size}
            appearance="primary"
            disabled={!isValid}
            icon={<Check />}
            onClick={handleSave}
          />
          <Button
            variant="text"
            size={size}
            appearance="danger"
            icon={<XClose />}
            onClick={handleCancel}
          />
        </>
      ) : (
        <>
          {children || text}
          <Edit03
            onClick={() => {
              setIsEditing(true);
              setValue(text || '');
            }}
            className="cursor-pointer"
          />
        </>
      )}
    </div>
  );
};
